import { type ReactNode } from "react";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ModalShell } from "@/components/ui/modal-shell";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import {
  ConciergeRequestForm,
  getConciergeErrorMessage,
  type ConciergeFormSource,
  type ConciergePrefill,
} from "@/components/concierge/ConciergeRequestForm";

export type ConciergeSource = Extract<ConciergeFormSource, "hero" | "inline">;
export type { ConciergePrefill };
export { getConciergeErrorMessage };

interface ConciergeRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: ConciergeSource;
  prefill?: ConciergePrefill;
}

export function ConciergeRequestModal({
  open,
  onOpenChange,
  source,
  prefill,
}: ConciergeRequestModalProps) {
  const { t } = useAppTranslation();
  const close = () => onOpenChange(false);
  const header: ReactNode = (
    <div className="relative border-b border-border bg-muted/30 px-5 py-5 sm:px-6">
      <button
        type="button"
        onClick={close}
        aria-label={t("common.close")}
        className="absolute right-3 top-3 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-gray-900 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
      >
        <X className="h-5 w-5" strokeWidth={2} />
      </button>
      <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
        {t("concierge.modal.eyebrow")}
      </span>
      <DialogPrimitive.Title className="mt-3 pr-10 text-xl font-bold leading-tight text-foreground sm:text-2xl">
        {t("concierge.modal.title")}
      </DialogPrimitive.Title>
      <DialogPrimitive.Description className="mt-2 text-sm text-muted-foreground">
        {t("concierge.modal.subtitle")}
      </DialogPrimitive.Description>
    </div>
  );

  return (
    <ModalShell
      open={open}
      onClose={close}
      title={t("concierge.modal.title")}
      description={t("concierge.modal.subtitle")}
      titleAlign="center"
      size="xl"
      padBody={false}
      data-testid="concierge-request-modal"
      customHeader={header}
    >
      <div className="px-5 py-5 sm:px-6">
        <ConciergeRequestForm
          variant="consumer"
          source={source}
          prefill={prefill}
          active={open}
          onClose={close}
        />
      </div>
    </ModalShell>
  );
}

export default ConciergeRequestModal;